import Cookies from "js-cookie";
import jwt_decode from "jwt-decode";
import { trackRouter } from "vue-gtag-next";
import { differenceInMinutes } from "date-fns";
import {
  createRouter,
  createWebHistory,
  isNavigationFailure,
} from "vue-router";

import { setToken, clearToken } from "@/utils/axios";
import { setSentryUser, clearSentryUser } from "@/plugins/sentry";
import { store } from "@/store";

import Home from "@/views/Home";
import Login from "@/views/Login";
import ChildView from "@/components/ChildView";

const routes = [
  {
    path: "/home",
    name: "Home",
    component: Home,
  },
  {
    path: "/",
    name: "Login",
    component: Login,
    meta: {
      layout: "Blank",
      guest: true,
      unnavigate: true,
    },
  },
  {
    path: "/plan/:id",
    name: "PlanDetails",
    component: () =>
      import(/* webpackChunkName: "PlanDetails" */ "@/views/Plan/Details.vue"),
  },
  {
    path: "/password-change",
    name: "ChangePass",
    component: () =>
      import(
        /* webpackChunkName: "ChangePass" */ "@/views/ChangePass/index.vue"
      ),
  },
  {
    path: "/new-user",
    name: "NewUser",
    component: () =>
      import(/* webpackChunkName: "NewUser" */ "@/views/NewUser/Index.vue"),
    meta: {
      layout: "Blank",
      guest: true,
    },
  },
  {
    path: "/password-reset",
    name: "ResetPass",
    component: () =>
      import(
        /* webpackChunkName: "ResetPass" */ "@/views/Guest/PasswordReset.vue"
      ),
    meta: {
      layout: "Blank",
      guest: true,
    },
  },
  {
    path: "/profile",
    name: "Profile",
    component: () =>
      import(/* webpackChunkName: "profile" */ "@/views/Profile.vue"),
  },
  {
    path: "/info/:id?",
    name: "LeadForm",
    meta: {
      layout: "Blank",
      guest: true,
    },
    component: () =>
      import(
        // /* webpackChunkName: "LeadForm" */ "@/views/ContractWizardWorkflow/index.vue"
        /* webpackChunkName: "LeadForm" */ "@/views/ContractWizardFlow/index.vue"
      ),
  },
  {
    path: "/quick-payment/:id?",
    component: () =>
      import(
        /* webpackChunkName: "quick-payment-main" */ "@/views/Guest/QuickPayment/MainMultiple.vue"
      ),
    name: "QuickPayment",
    meta: {
      layout: "Blank",
      guest: true,
    },
  },
  {
    path: "/payment-success",
    name: "QuickPaymentSuccess",
    meta: {
      layout: "Blank",
      guest: true,
    },
    component: () =>
      import(
        /* webpackChunkName: "quick-payment-sucess" */ "@/views/Guest/QuickPayment/PaymentSuccess.vue"
      ),
  },
  {
    path: "/payment-fail",
    name: "QuickPaymentFail",
    meta: {
      layout: "Blank",
      guest: true,
    },
    component: () =>
      import(
        /* webpackChunkName: "quick-payment-fail" */ "@/views/Guest/QuickPayment/PaymentFail.vue"
      ),
  },
  {
    path: "/support",
    component: ChildView,
    children: [
      {
        name: "SupportIndex",
        path: "",
        component: () =>
          import(
            /* webpackChunkName: "SupportIndex" */ "@/views/Support/Index.vue"
          ),
      },
      {
        name: "SupportAdd",
        path: "new",
        component: () =>
          import(
            /* webpackChunkName: "SupportAdd" */ "@/views/Support/Add.vue"
          ),
      },
    ],
  },
  {
    path: "/invoices",
    component: ChildView,
    children: [
      {
        path: ":id",
        name: "InvoicesView",
        component: () =>
          import(
            /* webpackChunkName: "InvoicesView" */ "@/views/Invoices/View.vue"
          ),
      },
      {
        path: ":id/pay",
        name: "InvoicesPay",
        component: () =>
          import(
            /* webpackChunkName: "InvoicesPay" */ "@/views/Invoices/Pay.vue"
          ),
      },
      {
        path: ":id/quick-payment/:service_id",
        name: "QuickPaymentInvoices",
        component: () =>
          import(
            /* webpackChunkName: "QuickPaymentInvoices" */ "@/views/Invoices/QuickPayment.vue"
          ),
      },
    ],
  },
  {
    path: "/address",
    name: "AddressForm",
    meta: {
      layout: "Blank",
      guest: true,
    },
    component: () =>
      import(
        /* webpackChunkName: "address-form" */ "@/views/Guest/Contract/AddressForm.vue"
      ),
  },
  {
    path: "/contract",
    name: "ContractForm",
    meta: {
      layout: "Blank",
      guest: true,
    },
    component: () =>
      import(
        /* webpackChunkName: "contract-form" */ "@/views/Guest/Contract/ContractForm.vue"
      ),
  },
  {
    // Vista interna para verificar que Sentry recibe eventos
    path: "/sentry-test",
    name: "SentryTest",
    meta: {
      guest: true,
    },
    component: () =>
      import(/* webpackChunkName: "SentryTest" */ "@/views/SentryTest.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

router.beforeEach(async (to, from, next) => {
  const jwt = Cookies.get("g_at");
  const refreshToken = Cookies.get("g_rt");
  let autenticado = false;

  store.APP_TITLE = "";
  store.APP_BACK_BUTTON = false;
  store.I118N_TITLE = true;

  if (jwt) {
    const decoded = jwt_decode(jwt);
    const sub = decoded.sub;
    let stringJSONValido = sub.replace(/'/g, '"');
    const user = JSON.parse(stringJSONValido);
    const { metadata } = user;
    const d = differenceInMinutes(decoded.exp * 1000, Date.now());
    if (d <= 5) {
      if (d <= 0) {
        clearToken();
        clearSentryUser();
        autenticado = false;
      }
      // const { data } = await axios.post("/auth/refresh");
      // setToken(data.token);
    } else {
      // Check if user is authenticated
      if (metadata.type === "user") {
        setToken({ access_token: jwt, refresh_token: refreshToken });
        store.user = metadata;
        setSentryUser(metadata);
        autenticado = true;
      } else {
        // Clear token with public user
        clearToken();
        clearSentryUser();
        autenticado = false;
      }
    }
  }

  if (!to.meta.guest) {
    if (autenticado) {
      next();
    } else {
      next({
        name: "Login",
        query: { redirectFrom: to.fullPath },
      });
    }
  } else {
    if (to.name === "Login" && autenticado) {
      next("/home");
    } else {
      next();
    }
  }
});

router.afterEach((to, from, failure) => {
  // afterEach también corre cuando la navegación falla. El caso típico es pulsar
  // en el menú la ruta en la que ya estás ("duplicated"): la vista no se vuelve
  // a montar, así que su onMounted -quien apaga APP_LOADING- nunca corre y el
  // spinner se quedaría encendido para siempre.
  if (isNavigationFailure(failure)) return;

  store.APP_TITLE = `titles.${to.name}`;
  store.APP_BACK_BUTTON = false;
  store.APP_LOADING = true;
});

// Cada despliegue borra del servidor los chunks de la versión anterior, así que
// una pestaña abierta desde antes falla al entrar a una ruta con carga diferida.
// Recargamos contra la URL destino para traer el index.html nuevo.
const CHUNK_LOAD_ERROR =
  /loading (css )?chunk .* failed|failed to fetch dynamically imported module|importing a module script failed/i;
const RELOAD_KEY = "chunk_reload_at";

router.onError((error, to) => {
  if (!CHUNK_LOAD_ERROR.test(error?.message || "")) return;

  // Sin esta guarda, un fallo que no venga de una versión desfasada dejaría al
  // usuario en un bucle de recargas.
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < 10000) return;
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch (e) {
    // sessionStorage bloqueado (modo privado): seguimos, recargar es lo correcto
  }

  window.location.assign(to.fullPath);
});

trackRouter(router);

export default router;
